import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ButtonDestinationEditor } from "../inspector/LandingInspectorControls";
import LandingCopyTextField from "../inspector/LandingCopyTextField";
import { persistedCssColorError, registryFieldError } from "../inspector/inspectorModel";
import { VisualColorControl, VisualSliderControl } from "../inspector/VisualSettingControls";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { buildInternalDestinationOptions, effectiveSectionPalette, getValueAtDescriptorPath } from "../elementEditing";

// WP-STANDARD | NAVBAR CONTEXTUAL GUIDE ANCHORS
// STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_RUNTIME_PENDING
// Anchors wrap existing controls only; they never own values or commit behavior.

const HERO_CTA_STYLE_LABELS = Object.freeze({
  textColor: "Color del texto",
  backgroundColor: "Color de fondo",
  backgroundOpacity: "Opacidad de fondo",
  hoverTextColor: "Texto al pasar el mouse",
  hoverBackgroundColor: "Fondo al pasar el mouse",
  hoverBackgroundOpacity: "Opacidad al pasar el mouse",
  borderColor: "Color del borde",
  borderWidth: "Grosor del borde",
  borderRadius: "Redondeado",
  fontSize: "Tamaño del texto",
  fontWeight: "Peso tipográfico",
  minWidth: "Ancho mínimo",
  minHeight: "Alto mínimo",
  paddingX: "Espaciado horizontal",
  paddingY: "Espaciado vertical",
  shadowColor: "Color de sombra",
  shadowOpacity: "Opacidad de sombra",
  shadowX: "Sombra horizontal",
  shadowY: "Sombra vertical",
  shadowBlur: "Desenfoque de sombra",
  shadowSpread: "Expansión de sombra",
});

function buttonFieldLabel(field) {
  const key = String(field?.path || "").split(".").pop();
  if (String(field?.path || "").startsWith("hero.") && HERO_CTA_STYLE_LABELS[key]) return HERO_CTA_STYLE_LABELS[key];
  return field?.label || key || "Valor";
}

function heroNumberFallback(field, descriptor) {
  const path = String(field?.path || "");
  const key = path.split(".").pop();
  const secondary = path.includes("hero.secondaryCtaStyle.");
  const mobile = descriptor?.previewDevice === "mobile";
  const defaults = {
    backgroundOpacity: secondary ? 0.08 : 1,
    hoverBackgroundOpacity: secondary ? 0.18 : 1,
    borderWidth: secondary ? 1.5 : 0,
    borderRadius: 999,
    fontSize: 14,
    minWidth: mobile ? 0 : 190,
    minHeight: mobile ? 46 : 48,
    paddingX: mobile ? 19 : 28,
    paddingY: 10,
    shadowOpacity: secondary ? 0 : 0.28,
    shadowX: 0,
    shadowY: secondary ? 0 : 10,
    shadowBlur: secondary ? 0 : 28,
    shadowSpread: 0,
  };
  return Object.prototype.hasOwnProperty.call(defaults, key) ? defaults[key] : (field?.limits?.min ?? 0);
}

function heroPreviewColorWithOpacity(color, opacity = 1) {
  const candidate = String(color || "").trim();
  const alpha = Math.max(0, Math.min(1, Number.isFinite(Number(opacity)) ? Number(opacity) : 1));
  if (!candidate || candidate.toLowerCase() === "transparent" || alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  const match = candidate.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (match) {
    const raw = match[1].length === 3 ? match[1].split("").map((char) => `${char}${char}`).join("") : match[1];
    return `rgba(${parseInt(raw.slice(0, 2), 16)}, ${parseInt(raw.slice(2, 4), 16)}, ${parseInt(raw.slice(4, 6), 16)}, ${alpha})`;
  }
  return `color-mix(in srgb, ${candidate} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

function heroButtonPreviewSx(form, descriptor) {
  const labelPath = String(descriptor?.labelPath || descriptor?.labelField?.path || "");
  if (!labelPath.startsWith("hero.")) return null;
  const secondary = labelPath.includes("secondaryCtaLabel");
  const style = secondary ? (form?.hero?.secondaryCtaStyle || {}) : (form?.hero?.primaryCtaStyle || {});
  const colors = effectiveSectionPalette(form, descriptor);
  const background = String(style.backgroundColor || "").trim() || (secondary ? "#ffffff" : colors.primary);
  const textColor = String(style.textColor || "").trim() || (secondary ? colors.onSecondary : colors.onPrimary);
  const borderColor = String(style.borderColor || "").trim() || colors.onSecondary;
  const borderWidth = style.borderWidth === "" || style.borderWidth == null ? (secondary ? 1.5 : 0) : Number(style.borderWidth);
  const radius = style.borderRadius === "" || style.borderRadius == null ? 999 : Number(style.borderRadius);
  const backgroundOpacity = style.backgroundOpacity === "" || style.backgroundOpacity == null ? (secondary ? 0.08 : 1) : Number(style.backgroundOpacity);
  const shadowOpacity = style.shadowOpacity === "" || style.shadowOpacity == null ? (secondary ? 0 : 0.28) : Number(style.shadowOpacity);
  const shadowX = style.shadowX === "" || style.shadowX == null ? 0 : Number(style.shadowX);
  const shadowY = style.shadowY === "" || style.shadowY == null ? (secondary ? 0 : 10) : Number(style.shadowY);
  const shadowBlur = style.shadowBlur === "" || style.shadowBlur == null ? (secondary ? 0 : 28) : Number(style.shadowBlur);
  const shadowSpread = style.shadowSpread === "" || style.shadowSpread == null ? 0 : Number(style.shadowSpread);
  const shadowColor = String(style.shadowColor || "").trim() || "#000000";
  return {
    mt: 0.8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    px: style.paddingX === "" || style.paddingX == null ? 3.5 : `${Number(style.paddingX)}px`,
    py: style.paddingY === "" || style.paddingY == null ? 1.2 : `${Number(style.paddingY)}px`,
    minWidth: style.minWidth === "" || style.minWidth == null ? 190 : `${Number(style.minWidth)}px`,
    minHeight: style.minHeight === "" || style.minHeight == null ? 48 : `${Number(style.minHeight)}px`,
    borderRadius: `${radius}px`,
    bgcolor: heroPreviewColorWithOpacity(background, backgroundOpacity),
    color: textColor,
    border: `${borderWidth}px ${borderWidth > 0 ? "solid" : "none"} ${borderColor}`,
    boxShadow: shadowOpacity > 0
      ? `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowSpread}px ${heroPreviewColorWithOpacity(shadowColor, shadowOpacity)}`
      : "none",
    fontSize: style.fontSize === "" || style.fontSize == null ? undefined : `${Number(style.fontSize)}px`,
    fontWeight: style.fontWeight || 900,
    textTransform: "none",
    maxWidth: "100%",
  };
}
function ctaColorFallback(field, form, descriptor) {
  if (field?.explicitVisual && String(field?.defaultValue || "").trim()) return String(field.defaultValue).trim();
  const path = String(field?.path || "");
  const secondary = path.includes("hero.secondaryCtaStyle.");
  const key = path.split(".").pop();
  const colors = effectiveSectionPalette(form, descriptor);

  if (key === "textColor") return secondary ? colors.onSecondary : colors.onPrimary;
  if (key === "hoverTextColor") return secondary ? colors.onSecondary : colors.onPrimary;
  if (key === "backgroundColor") return secondary ? "#ffffff" : colors.primary;
  if (key === "hoverBackgroundColor") return secondary ? "#ffffff" : colors.secondary;
  if (key === "borderColor") return colors.onSecondary;
  if (key === "shadowColor") return "#000000";
  return field?.fallbackValue || field?.overrideSeedValue || "#000000";
}

function ctaColorContractError(field, value) {
  return registryFieldError(field, value) || persistedCssColorError(value, field?.maxLength || 100);
}

function CtaColorFieldControl({ field, form, descriptor, disabled, onCommit }) {
  const rawValue = getValueAtDescriptorPath(form, field.path, "");
  const ownValue = String(rawValue ?? "").trim();
  const fallback = ctaColorFallback(field, form, descriptor);
  return (
    <VisualColorControl
      label={buttonFieldLabel(field)}
      value={ownValue}
      fallback={fallback}
      disabled={disabled}
      helperText={field.explicitVisual
        ? (field.path?.startsWith("navigation.megaMenuVisual.") ? "Valor visual explícito del Mega Menú." : "Color explícito de este botón.")
        : (ownValue ? "Color propio del CTA." : "Sin override, usa el estilo heredado del botón.")}
      allowExactCss
      validateExactValue={(candidate) => ctaColorContractError(field, candidate)}
      exactValueMaxLength={field.maxLength || 100}
      exactValueHelperText={field.explicitVisual
        ? "Admite HEX, rgb/rgba, hsl/hsla y transparent. Restablecer vuelve al valor explícito por defecto."
        : "Admite HEX, rgb/rgba, hsl/hsla y transparent. Vacío vuelve a heredar."}
      onChange={(next) => onCommit?.(field.path, next)}
      onReset={() => onCommit?.(field.path, field.explicitVisual ? (field.defaultValue || fallback) : "")}
    />
  );
}

function FieldControl({ field, form, baselineForm, descriptor, disabled, onCommit }) {
  if (!field) return null;
  const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return <FormControlLabel control={<Switch checked={value !== false} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.checked)} />} label={field.label} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{buttonFieldLabel(field)}</InputLabel>
        <Select label={buttonFieldLabel(field)} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    const isHeroStyleNumber = String(field.path || "").startsWith("hero.") && String(field.path || "").includes("CtaStyle.");
    if (isHeroStyleNumber) {
      return (
        <VisualSliderControl
          label={buttonFieldLabel(field)}
          value={value}
          min={field.limits?.min ?? 0}
          max={field.limits?.max ?? 100}
          step={field.limits?.step ?? 1}
          disabled={disabled}
          nullable
          fallbackValue={heroNumberFallback(field, descriptor)}
          inheritedLabel="Automático"
          resetLabel="Automático"
          onChange={(next) => onCommit?.(field.path, next)}
          onReset={() => onCommit?.(field.path, "")}
        />
      );
    }
    return <TextField fullWidth size="small" type="number" label={buttonFieldLabel(field)} value={value ?? ""} disabled={disabled} inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }} onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    return <CtaColorFieldControl field={field} form={form} descriptor={descriptor} disabled={disabled} onCommit={onCommit} />;
  }
  return <LandingCopyTextField fullWidth size="small" label={buttonFieldLabel(field)} field={field} path={field.path} form={form} baselineForm={baselineForm} value={value ?? ""} disabled={disabled} multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA} minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined} commitMode="change" onCommit={(nextValue) => onCommit?.(field.path, nextValue)} />;
}

export default function ButtonElementEditor({ descriptor, form, baselineForm, disabled, onCommit }) {
  const labelField = descriptor?.labelField;
  if (!labelField) return null;
  const labelValue = getValueAtDescriptorPath(form, labelField.path, "");
  const options = buildInternalDestinationOptions(form);
  const showField = descriptor?.showField;
  const requiresField = descriptor?.requiresProductsField || (descriptor?.extraFields || []).find((field) => /RequiresProducts$/.test(field?.path || ""));
  const remainingExtra = (descriptor?.extraFields || []).filter((field) => field !== requiresField);
  const styleFields = descriptor?.styleFields || [];
  const showIconField = descriptor?.showIconField;
  const iconField = descriptor?.iconField;
  const urlField = descriptor?.urlField;
  const behaviorField = descriptor?.behaviorField;
  const targetField = descriptor?.targetField;
  const sharedField = [labelField, urlField, showField, requiresField, behaviorField, targetField, ...remainingExtra]
    .find((field) => field?.shared);
  const megaMenuFields = descriptor?.megaMenuFields || [];
  const megaMenuContentFields = megaMenuFields.filter((field) => !field?.megaMenuGroup);
  const megaMenuVisualGroups = megaMenuFields.reduce((groups, field) => {
    if (!field?.megaMenuGroup) return groups;
    if (!groups[field.megaMenuGroup]) groups[field.megaMenuGroup] = [];
    groups[field.megaMenuGroup].push(field);
    return groups;
  }, {});
  const megaVisual = form?.navigation?.megaMenuVisual || {};

  return (
    <Stack spacing={1.35}>
      <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
        {(descriptor?.chips || []).map((chip) => <Chip key={chip} size="small" label={chip} variant="outlined" />)}
        {sharedField ? <Chip size="small" label="Se usa también en otras secciones" variant="outlined" /> : null}
        {requiresField && getValueAtDescriptorPath(form, requiresField.path, false) ? <Chip size="small" label="Catálogo" /> : null}
        {showIconField && getValueAtDescriptorPath(form, showIconField.path, false) ? <Chip size="small" label="Con icono" /> : null}
      </Stack>

      <Box sx={{ p: 1.2, borderRadius: 2.5, border: "1px solid rgba(25,36,47,.1)", bgcolor: "rgba(25,36,47,.025)" }}>
        <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.62)" }}>Vista previa</Typography>
        <Box sx={heroButtonPreviewSx(form, descriptor) || { mt: 0.8, display: "inline-flex", px: 2, py: 1, borderRadius: 999, bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 900 }}>
          {labelValue || "Botón"}
        </Box>
      </Box>

      <Box data-wp-navbar-guide-target="button-label">
        <LandingCopyTextField fullWidth size="small" label={labelField.label || "Texto del botón"} field={labelField} path={labelField.path} form={form} baselineForm={baselineForm} value={labelValue} disabled={disabled} commitMode="change" onCommit={(nextValue) => onCommit?.(labelField.path, nextValue)} />
      </Box>

      {showField ? (
        <Box data-wp-navbar-guide-target="button-visibility">
          <FieldControl descriptor={descriptor} field={showField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
        </Box>
      ) : null}
      {behaviorField ? (
        <Box data-wp-navbar-guide-target="button-behavior">
          <FieldControl descriptor={descriptor} field={behaviorField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
        </Box>
      ) : null}
      {targetField ? (
        <Box data-wp-navbar-guide-target="button-destination">
          <FieldControl descriptor={descriptor} field={targetField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
        </Box>
      ) : null}

      {urlField ? (
        <Box data-wp-navbar-guide-target="button-destination">
          {urlField.destinationPicker ? (
            <ButtonDestinationEditor field={urlField} value={getValueAtDescriptorPath(form, urlField.path, "")} options={options} disabled={disabled} onCommit={onCommit} />
          ) : <FieldControl descriptor={descriptor} field={urlField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />}
        </Box>
      ) : (!targetField ? <Chip size="small" label="Destino definido por esta sección" variant="outlined" sx={{ alignSelf: "flex-start" }} /> : null)}

      {requiresField ? <FieldControl descriptor={descriptor} field={requiresField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}
      {remainingExtra.map((field) => <FieldControl key={field.path} descriptor={descriptor} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}

      {showIconField || iconField ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Icono</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{showIconField ? <FieldControl descriptor={descriptor} field={showIconField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}{iconField && (!showIconField || getValueAtDescriptorPath(form, showIconField.path, true) !== false) ? <FieldControl descriptor={descriptor} field={iconField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}</Stack></AccordionDetails>
        </Accordion>
      ) : null}

      {(descriptor?.megaMenu || (descriptor?.megaMenuFields || []).length || (descriptor?.megaMenuOverrideFields || []).length) ? (
        <Accordion data-wp-navbar-guide-target="button-mega-menu" disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Configurar mega menú</Typography></AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {(descriptor?.megaMenuOverrideFields || []).map((field) => <FieldControl key={field.path} descriptor={descriptor} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}
              {megaMenuContentFields.map((field) => <FieldControl key={field.path} descriptor={descriptor} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}
              {Object.keys(megaMenuVisualGroups).length ? (
                <Box sx={{ mt: 0.5, p: 1.2, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5, bgcolor: megaVisual?.surface?.backgroundColor || "#F7F8FC" }}>
                  <Typography variant="overline" sx={{ color: megaVisual?.eyebrow?.color || "#E44E31", fontWeight: 900 }}>COLECCIÓN</Typography>
                  <Typography sx={{ color: megaVisual?.title?.color || "#494A98", fontWeight: 950, lineHeight: 1.15 }}>{form?.navigation?.megaMenuTitle || "Explorá el catálogo"}</Typography>
                  <Typography variant="caption" sx={{ color: megaVisual?.description?.color || "#5B5D73" }}>{form?.navigation?.megaMenuDescription || "Vista previa visual del Mega Menú"}</Typography>
                  <Stack direction="row" spacing={0.6} sx={{ mt: 1 }}>
                    <Box sx={{ px: 1, py: 0.45, borderRadius: 999, color: megaVisual?.categoryPill?.textColor || "#5B5D73", bgcolor: megaVisual?.categoryPill?.backgroundColor || "#EEF0F5", border: "1px solid", borderColor: megaVisual?.categoryPill?.borderColor || "transparent", fontSize: ".72rem", fontWeight: 800 }}>Categoría</Box>
                    <Box sx={{ px: 1, py: 0.45, borderRadius: 999, color: megaVisual?.allItemsButton?.textColor || "#494A98", bgcolor: megaVisual?.allItemsButton?.backgroundColor || "transparent", border: "1px solid", borderColor: megaVisual?.allItemsButton?.borderColor || "transparent", fontSize: ".72rem", fontWeight: 800 }}>Ver todos</Box>
                  </Stack>
                </Box>
              ) : null}
              {Object.entries(megaMenuVisualGroups).map(([group, fields]) => (
                <Accordion key={group} disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.08)", borderRadius: "10px !important", "&::before": { display: "none" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 850 }}>{group}</Typography></AccordionSummary>
                  <AccordionDetails><Stack spacing={1}>{fields.map((field) => <FieldControl key={field.path} descriptor={descriptor} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {styleFields.length ? (
        <Accordion data-wp-navbar-guide-target="button-appearance" disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia y hover</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{styleFields.map((field) => <FieldControl key={field.path} descriptor={descriptor} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
